import React, { useState, useEffect } from 'react';
import { useThemeMode } from '../../context/ThemeContext';
import { useRegion } from '../../context/RegionContext';
import { useLanguage } from '../../context/LanguageContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Container, Button, Box, IconButton, Drawer, List, ListItem, ListItemText,
  useTheme, useMediaQuery, Fab, Avatar, Menu, MenuItem, ListItemIcon, Divider, Typography
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../../context/AuthContext';
import { useSnackbar } from 'notistack';
import AIChatWidget from '../shared/AIChatWidget';

/**
 * Language selector with flag dropdown for the website header.
 * Shows current language flag with dropdown to switch languages.
 */
const WebsiteLangToggle = ({ sx = {}, variant = 'default' }) => {
  const { language, changeLanguage } = useLanguage();
  const [anchorEl, setAnchorEl] = useState(null);
  const isAr = (language || '').startsWith('ar');
  
  const languages = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'ar', label: 'العربية', flag: '🇶🇦' }
  ];
  
  const currentLang = languages.find(lang => lang.code === (isAr ? 'ar' : 'en')) || languages[0];
  
  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    handleClose();
  };
  
  // Mobile drawer variant (full width button style)
  if (variant === 'mobile') {
    return (
      <Box sx={{ ...sx }}>
        <Button
          onClick={handleOpen}
          fullWidth
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            borderRadius: '8px',
            py: 1,
            px: 2,
            justifyContent: 'flex-start',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.15)'
            }
          }}
        >
          <Typography sx={{ fontSize: '1.3rem', lineHeight: 1, mr: 1.5 }}>
            {currentLang.flag}
          </Typography>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
            {currentLang.label}
          </Typography>
        </Button>
        
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            elevation: 3,
            sx: {
              minWidth: 200,
              borderRadius: 2,
            }
          }}
        >
          {languages.map((lang) => (
            <MenuItem
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              selected={currentLang.code === lang.code}
              sx={{
                py: 1.5,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5
              }}
            >
              <Typography sx={{ fontSize: '1.3rem', lineHeight: 1 }}>
                {lang.flag}
              </Typography>
              <Typography
                sx={{
                  fontWeight: currentLang.code === lang.code ? 700 : 400,
                  fontSize: '0.9rem',
                  fontFamily: lang.code === 'ar' ? '"Noto Sans Arabic", Arial, sans-serif' : 'inherit'
                }}
              >
                {lang.label}
              </Typography>
            </MenuItem>
          ))}
        </Menu>
      </Box>
    );
  }
  
  // Default desktop variant (icon button)
  return (
    <Box sx={{ ...sx }}>
      <IconButton
        onClick={handleOpen}
        size="small"
        sx={{
          border: '1.5px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          px: 1,
          py: 0.5,
          '&:hover': {
            bgcolor: 'action.hover',
            borderColor: 'primary.main'
          }
        }}
      >
        <Typography sx={{ fontSize: '1.3rem', lineHeight: 1 }}>
          {currentLang.flag}
        </Typography>
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            minWidth: 160,
            borderRadius: 2,
            overflow: 'visible',
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          }
        }}
      >
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            selected={currentLang.code === lang.code}
            sx={{
              py: 1,
              px: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <Typography sx={{ fontSize: '1.3rem', lineHeight: 1 }}>
              {lang.flag}
            </Typography>
            <Typography
              sx={{
                fontWeight: currentLang.code === lang.code ? 700 : 400,
                fontSize: '0.9rem',
                fontFamily: lang.code === 'ar' ? '"Noto Sans Arabic", Arial, sans-serif' : 'inherit'
              }}
            >
              {lang.label}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

const WebsiteLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [avatarAnchor, setAvatarAnchor] = useState(null);
  const { mode } = useThemeMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg')); // Changed from 'md' to 'lg' for better tablet support
  const location = useLocation();
  const navigate = useNavigate();
  const { contact, legalBlurb } = useRegion();
  const { user, isAuthenticated, logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { isRTL } = useLanguage();
  // Show the EN/AR toggle for Qatar visitors (detected via region config defaultLanguage)
  const regionCfg = useRegion();
  // Always show language toggle for .qa domain, or when region config has defaultLanguage
  const showLangToggle = window.location.hostname.includes('fluxerp.qa') || Boolean(regionCfg?.defaultLanguage);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleLogout = async () => {
    setAvatarAnchor(null);
    await logout();
    enqueueSnackbar('Logged out successfully', { variant: 'info' });
    navigate('/login');
  };

  // Derive initials for avatar fallback
  const initials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'
    : '';

  const avatarBg = '#163300';
  const avatarColor = '#9FE870';

  const navItems = [
    { label: isRTL ? 'الرئيسية' : 'Home', path: '/home' },
    { label: isRTL ? 'المميزات' : 'Features', path: '/features' },
    { label: isRTL ? 'الأسعار' : 'Pricing', path: '/pricing' },
    { label: isRTL ? 'عن الشركة' : 'About', path: '/about' },
    { label: isRTL ? 'الأسئلة الشائعة' : 'FAQs', path: '/faqs' },
    { label: isRTL ? 'اتصل بنا' : 'Contact', path: '/contact' }
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: theme => theme.palette.mode === 'dark' ? 'background.default' : 'white',
      color: theme => theme.palette.mode === 'dark' ? 'text.primary' : 'inherit'
    }}>
      {/* Glassmorphic Navigation */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: theme.palette.mode === 'dark'
            ? '#000000'
            : 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid',
          borderColor: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.05)',
          color: theme.palette.mode === 'dark' ? 'primary.main' : 'text.primary'
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
            <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  height: 70,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  '& img': {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }
                }}>
                  <img
                    src={`/assets/logo-${mode === 'light' ? 'light' : 'dark'}.png`}
                    alt="FluxERP"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>

              </Box>
            </Link>

            {!isMobile ? (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'nowrap' }}>
                {navItems.map((item) => (
                  <Button
                    key={item.label}
                    component={Link}
                    to={item.path}
                    sx={{
                      color: location.pathname === item.path ? 'primary.main' : 'text.primary',
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      px: 1.5
                    }}
                  >
                    {item.label}
                  </Button>
                ))}

                {/* Language Toggle — desktop */}
                {showLangToggle && (
                  <WebsiteLangToggle sx={{ mx: 1 }} />
                )}

                {isAuthenticated ? (
                  /* ── Authenticated: avatar + dropdown ── */
                  <>
                    <IconButton
                      onClick={(e) => setAvatarAnchor(e.currentTarget)}
                      sx={{ ml: 1, p: 0.5 }}
                    >
                      <Avatar
                        sx={{
                          width: 38, height: 38,
                          bgcolor: avatarBg,
                          color: avatarColor,
                          fontWeight: 800,
                          fontSize: '0.9rem',
                          border: `2px solid ${avatarColor}`,
                        }}
                      >
                        {initials}
                      </Avatar>
                    </IconButton>

                    <Menu
                      anchorEl={avatarAnchor}
                      open={Boolean(avatarAnchor)}
                      onClose={() => setAvatarAnchor(null)}
                      transformOrigin={{ horizontal: isRTL ? 'left' : 'right', vertical: 'top' }}
                      anchorOrigin={{ horizontal: isRTL ? 'left' : 'right', vertical: 'bottom' }}
                      PaperProps={{
                        elevation: 4,
                        sx: {
                          mt: 1, minWidth: 200, borderRadius: 2,
                          overflow: 'visible',
                          '&::before': {
                            content: '""', display: 'block', position: 'absolute',
                            top: 0, right: isRTL ? 'auto' : 16, left: isRTL ? 16 : 'auto', width: 10, height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                          },
                        }
                      }}
                    >
                      {/* User info header */}
                      <Box sx={{ px: 2, py: 1.5 }}>
                        <Typography variant="subtitle2" fontWeight={700} noWrap>
                          {user?.firstName} {user?.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {user?.email}
                        </Typography>
                      </Box>
                      <Divider />
                      <MenuItem
                        onClick={() => { setAvatarAnchor(null); navigate('/dashboard'); }}
                        sx={{ py: 1.25 }}
                      >
                        <ListItemIcon><DashboardIcon fontSize="small" sx={{ color: '#163300' }} /></ListItemIcon>
                        {isRTL ? 'لوحة التحكم' : 'Dashboard'}
                      </MenuItem>
                      <MenuItem
                        onClick={() => { setAvatarAnchor(null); navigate('/settings'); }}
                        sx={{ py: 1.25 }}
                      >
                        <ListItemIcon><PersonIcon fontSize="small" sx={{ color: '#163300' }} /></ListItemIcon>
                        {isRTL ? 'إعدادات الملف الشخصي' : 'Profile Settings'}
                      </MenuItem>
                      <Divider />
                      <MenuItem
                        onClick={handleLogout}
                        sx={{ py: 1.25, color: 'error.main' }}
                      >
                        <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
                        {isRTL ? 'تسجيل الخروج' : 'Sign Out'}
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  /* ── Guest: Sign In + Get Demo ── */
                  <>
                    <Button component={Link} to="/login" variant="outlined" sx={{ ml: isRTL ? 0 : 2, mr: isRTL ? 2 : 0 }}>
                      {isRTL ? 'تسجيل الدخول' : 'Sign In'}
                    </Button>
                    <Button
                      component={Link}
                      to="/demo"
                      variant="contained"
                      color="primary"
                    >
                      {isRTL ? 'احجز عرضاً' : 'Get Demo'}
                    </Button>
                  </>
                )}
              </Box>
            ) : (
              <IconButton onClick={() => setMobileOpen(true)}>
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer — anchor flips for RTL */}
      <Drawer
        anchor={isRTL ? 'left' : 'right'}
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: '#163300',
            color: 'white'
          }
        }}
      >
        <Box sx={{ width: 280, p: 2, direction: isRTL ? 'rtl' : 'ltr' }}>
          <Box sx={{ display: 'flex', justifyContent: isRTL ? 'flex-start' : 'flex-end', mb: 2 }}>
            <IconButton onClick={() => setMobileOpen(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Language Toggle — mobile */}
          {showLangToggle && (
            <Box sx={{ px: 1, mb: 2 }}>
              <WebsiteLangToggle variant="mobile" />
            </Box>
          )}

          <List>
            {navItems.map((item) => (
              <ListItem
                key={item.path}
                button
                component={Link}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
              >
                <ListItemText primary={item.label} />
              </ListItem>
            ))}

            {isAuthenticated ? (
              /* ── Authenticated mobile menu ── */
              <>
                <Box sx={{ mx: 2, mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: avatarColor, color: avatarBg, fontWeight: 800, fontSize: '0.85rem' }}>
                      {initials}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={700} sx={{ color: 'white' }}>
                        {user?.firstName} {user?.lastName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        {user?.email}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <ListItem button component={Link} to="/dashboard" onClick={() => setMobileOpen(false)}
                  sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                  <ListItemIcon sx={{ minWidth: 36 }}><DashboardIcon sx={{ color: avatarColor, fontSize: '1.1rem' }} /></ListItemIcon>
                  <ListItemText primary={isRTL ? 'لوحة التحكم' : 'Dashboard'} />
                </ListItem>
                <ListItem button component={Link} to="/settings" onClick={() => setMobileOpen(false)}
                  sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                  <ListItemIcon sx={{ minWidth: 36 }}><PersonIcon sx={{ color: avatarColor, fontSize: '1.1rem' }} /></ListItemIcon>
                  <ListItemText primary={isRTL ? 'إعدادات الملف الشخصي' : 'Profile Settings'} />
                </ListItem>
                <ListItem button onClick={() => { setMobileOpen(false); handleLogout(); }}
                  sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                  <ListItemIcon sx={{ minWidth: 36 }}><LogoutIcon sx={{ color: '#ef4444', fontSize: '1.1rem' }} /></ListItemIcon>
                  <ListItemText primary={isRTL ? 'تسجيل الخروج' : 'Sign Out'} sx={{ color: '#ef4444' }} />
                </ListItem>
              </>
            ) : (
              /* ── Guest mobile menu ── */
              <>
                <ListItem
                  button
                  component={Link}
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }, mt: 2, borderTop: '1px solid rgba(255,255,255,0.1)', pt: 2 }}
                >
                  <ListItemText primary={isRTL ? 'تسجيل الدخول' : 'Sign In'} />
                </ListItem>
                <ListItem
                  button
                  component={Link}
                  to="/demo"
                  onClick={() => setMobileOpen(false)}
                  sx={{
                    bgcolor: '#9FE870',
                    mt: 1,
                    borderRadius: 1,
                    '&:hover': { bgcolor: '#8FD760' }
                  }}
                >
                  <ListItemText primary={isRTL ? 'احجز عرضاً' : 'Get Demo'} primaryTypographyProps={{ sx: { textAlign: 'center', color: '#163300', fontWeight: 700 } }} />
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>



      {/* Footer */}
      <Box
        component="footer"
        sx={{
          bgcolor: '#0E0F0C',
          color: 'white',
          py: 8,
          mt: 0,
          direction: isRTL ? 'rtl' : 'ltr',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 4 }}>

            {/* Brand column */}
            <Box>
              <Box sx={{ fontWeight: 700, fontSize: 20, mb: 1 }}>FluxERP</Box>
              <Box sx={{ opacity: 0.8, fontSize: 14, mb: 2 }}>
                {isRTL
                  ? 'نظام ERP سحابي متكامل ومتوافق مع معايير IFRS'
                  : 'Enterprise SaaS ERP & IFRS-Compliant Financials'}
              </Box>
              <Box sx={{ opacity: 0.8, fontSize: 12 }}>
                {legalBlurb}
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <IconButton href="https://www.facebook.com/FluxERPGlobal" target="_blank" sx={{ color: 'white', '&:hover': { color: '#1877F2' } }}>
                  <FacebookIcon />
                </IconButton>
                <IconButton href="https://twitter.com" target="_blank" sx={{ color: 'white', '&:hover': { color: '#1DA1F2' } }}>
                  <TwitterIcon />
                </IconButton>
                <IconButton href="https://instagram.com" target="_blank" sx={{ color: 'white', '&:hover': { color: '#E4405F' } }}>
                  <InstagramIcon />
                </IconButton>
                <IconButton href="https://linkedin.com/company/fluxerpglobal" target="_blank" sx={{ color: 'white', '&:hover': { color: '#0A66C2' } }}>
                  <LinkedInIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Product column */}
            <Box>
              <Box sx={{ fontWeight: 600, mb: 2 }}>{isRTL ? 'المنتج' : 'Product'}</Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link to="/features" style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}>
                  {isRTL ? 'المميزات' : 'Features'}
                </Link>
                <Link to="/pricing" style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}>
                  {isRTL ? 'الأسعار' : 'Pricing'}
                </Link>
                <Link to="/api-docs" style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}>
                  {isRTL ? 'توثيق API' : 'API Documentation'}
                </Link>
                <a
                  href={`${(process.env.REACT_APP_API_BASE_URL || '').replace('/api', '')}${isAuthenticated ? '/app/user-manual/overview' : '/docs/user-manual/overview'}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}
                >
                  {isRTL ? 'دليل المستخدم' : 'User Manual'}
                </a>
                <Link to="/blog" style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}>
                  {isRTL ? 'المدونة' : 'Blog'}
                </Link>
                <Link to="/demo" style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}>
                  {isRTL ? 'طلب عرض تجريبي' : 'Request Demo'}
                </Link>
              </Box>
            </Box>

            {/* Company column */}
            <Box>
              <Box sx={{ fontWeight: 600, mb: 2 }}>{isRTL ? 'الشركة' : 'Company'}</Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link to="/about" style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}>
                  {isRTL ? 'عن الشركة' : 'About'}
                </Link>
                <Link to="/contact" style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}>
                  {isRTL ? 'اتصل بنا' : 'Contact'}
                </Link>
                <Link to="/faqs" style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}>
                  {isRTL ? 'الأسئلة الشائعة' : 'FAQs'}
                </Link>
                <Link to="/partner" style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}>
                  {isRTL ? 'كن شريكاً' : 'Become a Partner'}
                </Link>
              </Box>
            </Box>

            {/* Legal column */}
            <Box>
              <Box sx={{ fontWeight: 600, mb: 2 }}>{isRTL ? 'القانونية' : 'Legal'}</Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link to="/privacy" style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}>
                  {isRTL ? 'سياسة الخصوصية' : 'Privacy Policy'}
                </Link>
                <Link to="/terms" style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}>
                  {isRTL ? 'شروط الخدمة' : 'Terms of Service'}
                </Link>
                <Link to="/cookie-policy" style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}>
                  {isRTL ? 'سياسة ملفات تعريف الارتباط' : 'Cookie Policy'}
                </Link>
                <Link to="/dpa" style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}>
                  DPA
                </Link>
                <Link to="/security" style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}>
                  {isRTL ? 'الأمان' : 'Security'}
                </Link>
                <Link to="/status" style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}>
                  {isRTL ? 'حالة النظام' : 'System Status'}
                </Link>
                <Box
                  onClick={() => window.dispatchEvent(new CustomEvent('reopenCookieSettings'))}
                  style={{ color: 'white', opacity: 0.8, textDecoration: 'none', cursor: 'pointer', fontSize: '1rem' }}
                >
                  {isRTL ? 'إعدادات ملفات الارتباط' : 'Cookie Settings'}
                </Box>
              </Box>
            </Box>

          </Box>

          {/* Copyright bar */}
          <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', mt: 6, pt: 4, textAlign: 'center', opacity: 0.6 }}>
            {isRTL
              ? `© ${new Date().getFullYear()} Flux IT Studio LTD. جميع الحقوق محفوظة.`
              : `© ${new Date().getFullYear()} Flux IT Studio LTD. All rights reserved.`}
          </Box>
        </Container>
      </Box>


      {/* Public AI Knowledge Assistant */}
      <AIChatWidget publicMode />
    </Box>
  );
};

export default WebsiteLayout;
